import { useState, useEffect, useCallback } from 'react';
import { getKidsRotations, saveKidsRotations, getKidsGroups } from '../../utils/api';
import { BrandSelect, GroupPill, textOn, KIDS_TEAL } from './kidsUi';

// Rotations — which group is at which activity each session, per program.
// Groups come from the Small Groups tab (one list, one set of colors — set a
// group's color there and it shows everywhere). Sessions and activities are
// edited here in Build mode. Views: Day (default) / Group / Activity, each
// rendered as outlined rounded cards in a multi-column grid.

const PROGRAMS = ['Elementary', '5-6', '3-4'];
const TABS = [
  { id: 'day', label: 'Day view' },
  { id: 'group', label: 'Group view' },
  { id: 'activity', label: 'Activity view' },
  { id: 'build', label: 'Build' },
];

function emptyData(program) {
  return { program, sessions: [], activities: [], groups: [], grid: {} };
}

export default function KidsRotationsTab({ showToast, readOnly }) {
  const [program, setProgram] = useState(PROGRAMS[0]);
  const [data, setData] = useState(emptyData(PROGRAMS[0]));
  const [groups, setGroups] = useState([]);
  const [mode, setMode] = useState('day');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [newSession, setNewSession] = useState('');
  const [newActivity, setNewActivity] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getKidsRotations(program).catch(() => null),
      getKidsGroups().catch(() => []),
    ]).then(([d, g]) => {
      if (cancelled) return;
      setData(d && d.sessions ? d : emptyData(program));
      setGroups(g || []);
      setDirty(false);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [program]);

  const update = useCallback((mut) => {
    setData(prev => { const next = JSON.parse(JSON.stringify(prev)); mut(next); return next; });
    setDirty(true);
  }, []);

  const programGroups = groups.filter(g => !g.program || g.program === program);
  const groupNames = programGroups.map(g => g.name);
  const colorFor = name => {
    const g = programGroups.find(x => x.name === name) || groups.find(x => x.name === name);
    return g?.color || KIDS_TEAL;
  };

  function addItem(kind, value, clear) {
    const v = value.trim();
    if (!v) return;
    update(d => { if (!d[kind].includes(v)) d[kind].push(v); });
    clear('');
  }
  function removeItem(kind, value) {
    update(d => {
      d[kind] = d[kind].filter(x => x !== value);
      if (kind === 'sessions') delete d.grid[value];
      if (kind === 'activities') Object.keys(d.grid).forEach(s => { if (d.grid[s]) delete d.grid[s][value]; });
    });
  }
  function setCell(session, activity, group) {
    update(d => { if (!d.grid[session]) d.grid[session] = {}; d.grid[session][activity] = group; });
  }

  async function handleSave() {
    setSaving(true);
    try {
      await saveKidsRotations(program, { ...data, groups: groupNames });
      setDirty(false);
      showToast('Rotations saved.');
    } catch (e) {
      showToast('Save failed — try again.');
    } finally {
      setSaving(false);
    }
  }

  const { sessions, activities, grid } = data;
  const cell = (s, a) => (grid[s] && grid[s][a]) || '';
  const dayOf = s => (s.replace(/\s+\d+\s*$/, '').trim() || s);
  const days = [];
  sessions.forEach(s => { const d = dayOf(s); if (!days.includes(d)) days.push(d); });
  const empty = sessions.length === 0 || activities.length === 0;

  const gridStyle = min => ({ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${min}px, 1fr))`, gap: 16, alignItems: 'start' });
  const cardStyle = { border: '1px solid var(--border)', borderRadius: 8, padding: 16, background: '#fff' };
  const mTh = { fontFamily: 'var(--font-h)', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--muted)', textAlign: 'left', padding: '0 10px 8px 0', borderBottom: '1px solid var(--border)' };
  const mTd = { fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--mid)', padding: '8px 10px 8px 0', borderBottom: '1px solid var(--border)' };
  const mTdName = { ...mTd, fontFamily: 'var(--font-h)', fontWeight: 700, color: 'var(--black)' };
  const pillHdr = name => {
    const c = colorFor(name);
    return <span className="pill" style={{ background: c, color: textOn(c), display: 'inline-block', marginBottom: 12 }}>{name}</span>;
  };

  return (
    <div className="page-content">
      <div className="page-title-row">
        <div>
          <div className="page-title">Rotations</div>
          <div className="page-sub">Which group is at which activity each session · groups &amp; colors come from Small Groups</div>
        </div>
      </div>

      <div className="panel">
        <div className="panel-header" style={{ flexWrap: 'wrap', gap: 8 }}>
          <BrandSelect value={program} options={PROGRAMS} onChange={setProgram} size="sm" />
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {TABS.filter(t => !readOnly || t.id !== 'build').map(t => (
              <button key={t.id} className={`tv-tab${mode === t.id ? ' active' : ''}`} style={{ padding: '6px 12px' }} onClick={() => setMode(t.id)}>{t.label}</button>
            ))}
          </div>
        </div>

        <div style={{ padding: '20px 24px', overflowX: 'auto' }}>
          {loading ? (
            <div style={{ color: 'var(--muted)', fontFamily: 'var(--font-b)', fontSize: 13 }}>Loading…</div>
          ) : mode === 'build' ? (
            <>
              <div className="two-col" style={{ marginBottom: 24 }}>
                {[
                  { kind: 'sessions', label: 'Sessions', val: newSession, set: setNewSession, ph: 'e.g. Friday 1' },
                  { kind: 'activities', label: 'Activities', val: newActivity, set: setNewActivity, ph: 'e.g. Games' },
                ].map(col => (
                  <div key={col.kind}>
                    <div className="form-label">{col.label}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                      {data[col.kind].map(v => (
                        <span key={v} className="pill pill-gray" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          {v}
                          <span style={{ cursor: 'pointer', fontWeight: 800 }} onClick={() => removeItem(col.kind, v)}>×</span>
                        </span>
                      ))}
                      {data[col.kind].length === 0 && <span style={{ fontFamily: 'var(--font-b)', fontSize: 12, color: 'var(--muted)' }}>None yet</span>}
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input className="form-input" style={{ fontSize: 13 }} placeholder={col.ph} value={col.val}
                        onChange={e => col.set(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && addItem(col.kind, col.val, col.set)} />
                      <button className="btn btn-sm btn-dark" onClick={() => addItem(col.kind, col.val, col.set)} disabled={!col.val.trim()}>Add</button>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ marginBottom: 20, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                <span className="form-label" style={{ margin: 0 }}>Groups</span>
                {groupNames.map(n => <GroupPill key={n} name={n} color={colorFor(n)} />)}
                {groupNames.length === 0 && <span style={{ fontFamily: 'var(--font-b)', fontSize: 12, color: 'var(--muted)' }}>None yet — add them on the Small Groups tab.</span>}
              </div>

              {empty ? (
                <div style={{ fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--muted)' }}>Add at least one session and one activity to build the grid.</div>
              ) : (
                <table className="data-table" style={{ minWidth: 480 }}>
                  <thead><tr><th>Session</th>{activities.map(a => <th key={a}>{a}</th>)}</tr></thead>
                  <tbody>
                    {sessions.map(s => (
                      <tr key={s} style={{ cursor: 'default' }}>
                        <td className="td-name">{s}</td>
                        {activities.map(a => (
                          <td key={a}>
                            <BrandSelect size="sm" width={120} value={cell(s, a)} options={['', ...groupNames]} labels={{ '': '—' }} onChange={g => setCell(s, a, g)} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
                <button className="btn btn-dark" onClick={handleSave} disabled={saving || !dirty}>
                  {saving ? 'Saving…' : dirty ? 'Save Rotations' : 'Saved'}
                </button>
                {dirty && <span style={{ fontFamily: 'var(--font-b)', fontSize: 12, color: 'var(--muted)' }}>Unsaved changes</span>}
              </div>
            </>
          ) : empty ? (
            <div style={{ fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--muted)' }}>Build the rotation first.</div>
          ) : mode === 'day' ? (
            <div style={gridStyle(300)}>
              {days.map(d => {
                const ds = sessions.filter(s => dayOf(s) === d);
                return (
                  <div key={d} style={cardStyle}>
                    <span className="pill" style={{ background: KIDS_TEAL, color: '#fff', display: 'inline-block', marginBottom: 12 }}>{d}</span>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead><tr><th style={mTh}>Session</th>{activities.map(a => <th key={a} style={mTh}>{a}</th>)}</tr></thead>
                        <tbody>
                          {ds.map(s => (
                            <tr key={s}><td style={mTdName}>{s}</td>{activities.map(a => <td key={a} style={mTd}>{cell(s, a) || '—'}</td>)}</tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : mode === 'group' ? (
            <div style={gridStyle(220)}>
              {groupNames.map(g => (
                <div key={g} style={cardStyle}>
                  {pillHdr(g)}
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr><th style={mTh}>Session</th><th style={mTh}>Activity</th></tr></thead>
                    <tbody>
                      {sessions.map(s => (
                        <tr key={s}>
                          <td style={mTdName}>{s}</td>
                          <td style={mTd}>{activities.find(a => cell(s, a) === g) || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
              {groupNames.length === 0 && <div style={{ fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--muted)' }}>Add groups on the Small Groups tab first.</div>}
            </div>
          ) : (
            <div style={gridStyle(220)}>
              {activities.map(a => (
                <div key={a} style={cardStyle}>
                  <span className="pill" style={{ background: KIDS_TEAL, color: '#fff', display: 'inline-block', marginBottom: 12 }}>{a}</span>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr><th style={mTh}>Session</th><th style={mTh}>Group</th></tr></thead>
                    <tbody>
                      {sessions.map(s => (
                        <tr key={s}><td style={mTdName}>{s}</td><td style={mTd}>{cell(s, a) || '—'}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
